import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Checkbox, Group, LoadingOverlay, Modal, Stack, Text, Title, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconFileDownload } from "@tabler/icons-react";
import dayjs from "dayjs";
import { ExportCampaignResultsParamsInput, ExportJob } from "src/graphql/generated/graphql";
import { checkExportJobState } from "src/utils/helpers";

import { createColumnHelper, DataGrid } from "../../components/DataGrid";
import { useCampaignResultsExportJobs } from "../../hooks/campaignResultsExportJobs";
import { useExportCampaignResults } from "../../hooks/exportCampaignResults";
import { createModal } from "../../hooks/modals";
import { useDownloadExportJob } from "../../hooks/system";

const columnHelper = createColumnHelper<ExportJob>();

type ExportContent = "all" | "images" | "videos";

/** Which media an export job contains, from its paramsJson; includeStamps is ignored. */
const getExportContent = (paramsJson?: string | null): ExportContent | undefined => {
    if (!paramsJson) {
        return undefined;
    }

    try {
        const { includeImages, includeVideos } = JSON.parse(paramsJson) ?? {};

        if (includeImages && includeVideos) {
            return "all";
        }
        if (includeImages) {
            return "images";
        }
        if (includeVideos) {
            return "videos";
        }
    } catch {
        // unparsable params: no content label
    }

    return undefined;
};

export interface ExportCampaignResultsModalProps {
    campaignId: string;
    onClose();
}

export const ExportCampaignResultsModal = createModal(({ campaignId, onClose }: ExportCampaignResultsModalProps) => {
    const { t } = useTranslation(["common"]);

    const { exportJobs, isLoadingExportJobs, reloadExportJobs } = useCampaignResultsExportJobs(campaignId);

    const columns = useMemo(
        () => [
            columnHelper.accessor("startedAt", {
                id: "startedat",
                header: t("date"),
                cell: (props) =>
                    props?.getValue() ? dayjs(props.getValue()).format("DD.MM.YYYY") : <Text color="dimmed">-</Text>,
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("state", {
                id: "state",
                header: t("exportjob_state"),
                cell: (props) => {
                    const value = props?.getValue();
                    return value && checkExportJobState(value) ? t(`exportjob_state_${value}`) : <Text color="dimmed">-</Text>;
                },
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("paramsJson", {
                id: "content",
                header: t("exportjob_content"),
                cell: (props) => {
                    const content = getExportContent(props?.getValue());
                    return content ? t(`exportjob_content_${content}`) : <Text color="dimmed">-</Text>;
                },
                footer: (props) => props.column.id,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (props) => (
                    <Tooltip label={t("exportjob_download")} withArrow>
                        <Button
                            variant="subtle"
                            color="orange"
                            disabled={props?.row?.original?.state !== "finished"}
                            onClick={() => handleDownloadExport(props?.row?.original?.id)}
                            leftIcon={<IconFileDownload size="1em" />}
                            compact
                            px="xs"
                        >
                            {t("download_title")}
                        </Button>
                    </Tooltip>
                ),
                footer: (props) => props.column.id,
            }),

            /* columnHelper.accessor("stats", {// TODO  currently backend return 0 bytes anyway
                id: "size",
                header: t("exportjob_size"),
                cell: (props) => {
                    const stats = props?.getValue();

                    if (!stats?.compressedBytes && !stats?.originalBytes) {
                        return <Text color="dimmed">-</Text>;
                    }

                    return (
                        <Text>
                            <Text weight={500} span>
                                {bytesToSize(stats.compressedBytes)}
                            </Text>{" "}
              (<Text span>{bytesToSize(stats.originalBytes)}</Text>)
                        </Text>
                    );
                },
                footer: (props) => props.column.id,
            }),*/
            // eslint-disable-next-line react-hooks/exhaustive-deps
        ],
        [t]
    );

    const paramsForm = useForm<ExportCampaignResultsParamsInput>({
        initialValues: {
            includeImages: true,
            includeVideos: true,
            includeStamps: false,
        },
    });

    const { exportCampaignResults, isExportingCampaignResults, jobId, jobUpdate } = useExportCampaignResults(campaignId);

    const submittedParams = useRef<ExportCampaignResultsParamsInput>();
    const [localJobs, setLocalJobs] = useState<ExportJob[]>([]);

    useEffect(() => {
        if (jobId) {
            setLocalJobs((jobs) => [
                ...jobs.filter((job) => job.id !== jobId),
                {
                    id: jobId,
                    state: "started",
                    startedAt: Date.now(),
                    paramsJson: JSON.stringify(submittedParams.current),
                } as ExportJob,
            ]);
            reloadExportJobs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    useEffect(() => {
        if (jobUpdate) {
            setLocalJobs((jobs) =>
                jobs.map((job) => (job.id === jobUpdate.jobId ? ({ ...job, state: jobUpdate.state } as ExportJob) : job))
            );
            reloadExportJobs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobUpdate]);

    // Fetched rows plus the local ones; a local finished/error beats a fetched started.
    const jobs = useMemo(() => {
        const byId = new Map<string, ExportJob>(exportJobs.map((job) => [job.id, job]));

        for (const local of localJobs) {
            const fetched = byId.get(local.id);

            if (!fetched) {
                byId.set(local.id, local);
            } else if (fetched.state === "started" && local.state !== "started") {
                byId.set(local.id, { ...fetched, state: local.state });
            }
        }

        return [...byId.values()].sort((logA, logB) => logB.startedAt - logA.startedAt);
    }, [exportJobs, localJobs]);

    const handleSubmit = useCallback(async (params: ExportCampaignResultsParamsInput) => {
        submittedParams.current = params;
        const downloadUrl = await exportCampaignResults(params);
        reloadExportJobs();

        if (downloadUrl) {
            window.open(downloadUrl, "_blank");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { downloadExportJob, isDownloadingExportJob } = useDownloadExportJob();

    const handleDownloadExport = useCallback(
        async (jobId: string) => {
            const downloadUrl = await downloadExportJob(jobId);

            if (!downloadUrl) {
                return;
            }

            window.open(downloadUrl, "_blank");
        },
        [downloadExportJob]
    );

    return (
        <Modal size="lg" title={<Title order={2}>{t("export_title")}</Title>} onClose={onClose} opened>
            <LoadingOverlay visible={isLoadingExportJobs || isDownloadingExportJob} />
            <Box component="form" onSubmit={paramsForm.onSubmit(handleSubmit)}>
                <Stack mb="xl" spacing="md">
                    <Group>
                        <Checkbox
                            label={t("campaign_results_export_includeimages")}
                            disabled={isExportingCampaignResults}
                            checked={paramsForm.values?.includeImages}
                            onChange={(event) => paramsForm.setFieldValue("includeImages", event?.target?.checked)}
                        />
                        <Checkbox
                            label={t("campaign_results_export_includevideos")}
                            disabled={isExportingCampaignResults}
                            checked={paramsForm.values?.includeVideos}
                            onChange={(event) => paramsForm.setFieldValue("includeVideos", event?.target?.checked)}
                        />
                    </Group>
                    <Button
                        type="submit"
                        loading={isExportingCampaignResults}
                        disabled={!paramsForm.values?.includeImages && !paramsForm.values?.includeVideos}
                    >
                        {isExportingCampaignResults ? t("campaign_export_preparing") : t("campaign_export_all_media")}
                    </Button>
                </Stack>
            </Box>
            <DataGrid
                data={jobs}
                columns={columns}
                isLoading={isLoadingExportJobs}
                getRowId={(record) => record?.id}
                mobileHeaderIndex={0}
                secMobileHeaderIndex={1}
            />
        </Modal>
    );
});
